/**
 * @module app.cd
 */

import * as CD from 'app/cd/models/essette-api';
import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { AuthService } from 'app/cd/services/auth-service';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { guid } from 'app/shared/lib/guid';

export interface IAuthSummaryModelData {
    auth: CD.Auth;
}

@autoinject()
export class AuthSummary extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected authId: string | null = null;

    protected auth: CD.Auth | null = null;

    protected targetId: string | null = this.authId;
    protected readonly: boolean = true;
    public includeInactive: boolean = false;
    public allowDeactivate: boolean = false;
    public allowFlagSelect: boolean = false;
    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'cd/auths',
        targetType: 'CD.Auth',
        moduleCode: 'CD'
    };

    protected id: guid = guid();

    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly service: AuthService,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    /// This is for use with the global Modal passed in with the compose element
    protected async activate(model: IAuthSummaryModelData): Promise<void> {
        if(!model || !model.auth?.id) { return; }

        this.authId = model.auth.id;
        this.reload()
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        if(this.authId) {
            await this.loadAuth(this.authId);
        }
    }


    ////****************** Functions ************************************************////



    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuth(authId: string): Promise<void> {
        if(!authId) return;

        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.service.loadAuth(authId, includes);
            this.auth = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}

/**
 * Opens the auth summary modal with the global dialog
 * @param auth The auth information to use
 */
export function openAuthSummary(auth: CD.Auth, moduleCode = 'cd'): void {
    if(!auth || !auth.id) {
        throw new Error('Auth is missing or invalid');
    }

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '11',
        helpTopic: `${moduleCode}.auth.summary`,
        title: {
            key: 'cd:routes.auth.summary.$title',
            replace: { number: auth.number }
        },
        print: true,
        popout: {
            route: `${moduleCode}.auth.summary`,
            params: { authId: auth.id }
        }
    };

    const modelData: IAuthSummaryModelData = {
        auth: auth
    };

    const modalData: IModalData = {
        componentId: 'app/cd/components/summary/auth/auth-summary',
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
